import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PropertyCard } from '@/components/property/PropertyCard';
import { ProjectFeedBanner } from '@/components/property/ProjectFeedBanner';
import { FeedStoriesRow } from '@/components/property/FeedStoriesRow';
import { buildFeed } from '@/lib/feedSequencer';
import { getAdSessionId, recordImpression } from '@/lib/adSession';

interface Props {
  items: any[];
  layout?: 'grid' | 'list';
  cardType?: 'property' | 'commercial' | 'plot';
  pageSize?: number;
  /** When true, project banners + stories row are inserted. Use only on page 1. */
  enableInjections?: boolean;
  /** Page slug for ad-event logging (e.g. 'buy', 'rent', 'plots'). */
  page?: string;
}

/**
 * Shared feed grid that applies the project-wide feed sequence
 * (Super Hot / Hot / Normal + Project Banners + Stories row after 8 slots)
 * across Buy, Rent, Commercial, and Plots pages.
 */
export function FeedGrid({
  items,
  layout = 'grid',
  cardType = 'property',
  pageSize = 25,
  enableInjections = true,
  page,
}: Props) {
  const sessionId = useMemo(() => getAdSessionId(), []);
  const trackedImpressions = useRef<Set<string>>(new Set());
  const trackedClicks = useRef<Set<string>>(new Set());
  const pageSlug = page || cardType;

  const { data: feedProjectCampaigns } = useQuery({
    queryKey: ['feed-project-banners-adops', cardType],
    enabled: enableInjections,
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      // Ad Operations–managed mid-search project banner: ad_campaigns
      // with placement = 'middle_banner_search' and a linked project.
      // Scheduling: only active within [start_date, end_date].
      // Ordering: highest priority first, then newest.
      const { data, error } = await (supabase as any)
        .from('ad_campaigns')
        .select('id, priority, target_url, image_url, linked_project_id, projects:linked_project_id (*, project_images(url, is_primary), project_unit_types(*), developer_profiles(company_name, phone, whatsapp, logo_url), agencies(name, logo_url, phone))')
        .eq('placement', 'middle_banner_search')
        .eq('is_active', true)
        .eq('status', 'active')
        .lte('start_date', today)
        .gte('end_date', today)
        .not('linked_project_id', 'is', null)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(2);
      if (error) throw error;
      return (data || []).filter((c: any) => c.projects);
    },
    staleTime: 5 * 60 * 1000,
  });

  const projectBanners = useMemo(
    () =>
      (feedProjectCampaigns || []).map((c: any) => ({
        ...c.projects,
        __ad_campaign_id: c.id,
        __ad_target_url: c.target_url,
      })),
    [feedProjectCampaigns]
  );

  // Fire one impression per campaign per session
  useEffect(() => {
    if (!enableInjections || !feedProjectCampaigns?.length) return;
    feedProjectCampaigns.forEach((c: any) => {
      const k = c.id + '|middle_banner_search';
      if (trackedImpressions.current.has(k)) return;
      trackedImpressions.current.add(k);
      recordImpression(c.id, 'middle_banner_search');
      (supabase as any)
        .from('ad_events')
        .insert({
          campaign_id: c.id,
          event_type: 'impression',
          placement: 'middle_banner_search',
          page: pageSlug,
          session_id: sessionId,
        })
        .then?.(() => {}, () => {});
    });
  }, [feedProjectCampaigns, enableInjections, pageSlug, sessionId]);

  const handleBannerClick = (campaignId: string) => {
    const k = campaignId + '|middle_banner_search';
    if (trackedClicks.current.has(k)) return;
    trackedClicks.current.add(k);
    (supabase as any)
      .from('ad_events')
      .insert({
        campaign_id: campaignId,
        event_type: 'click',
        placement: 'middle_banner_search',
        page: pageSlug,
        session_id: sessionId,
      })
      .then?.(() => {}, () => {});
  };

  const slots = buildFeed({
    properties: items,
    projectBanners: enableInjections ? projectBanners : [],
    pageSize,
    skipBanners: !enableInjections,
  });

  const colSpanWide = layout === 'grid' ? '' : '';
  const storiesSpanWide = layout === 'grid' ? 'sm:col-span-2 lg:col-span-3' : '';
  const gridCls = `grid gap-4 ${layout === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`;

  const nodes: React.ReactNode[] = [];
  let placed = 0;
  slots.forEach((slot) => {
    if (slot.kind === 'project-banner') {
      const campaignId = (slot.project as any).__ad_campaign_id as string | undefined;
      nodes.push(
        <div
          key={`banner-${slot.position}-${slot.project.id}`}
          className={colSpanWide}
          onClickCapture={() => campaignId && handleBannerClick(campaignId)}
        >
          <ProjectFeedBanner project={slot.project} layout={layout} />
        </div>
      );
    } else {
      nodes.push(
        <PropertyCard
          key={slot.property.id}
          property={slot.property}
          layout={layout}
          cardType={cardType}
        />
      );
    }
    placed += 1;
    if (enableInjections && placed === 8) {
      nodes.push(
        <div key="feed-stories-row" className={storiesSpanWide}>
          <FeedStoriesRow fallbackProperties={items} />
        </div>
      );
    }
  });

  return <div className={gridCls}>{nodes}</div>;
}
